import { DEXIE_STORE_NAMES } from './idbUtils'

const ONE_HOUR_MS = 60 * 60 * 1000

/**
 * Automated, read-only checks derived from the manual test suites in
 * docs/local-first-eval.md (TS-01, TS-02, TS-09) that don't require
 * simulating UI interaction. Pure function: receives already-fetched data
 * so it can be unit tested without mocking Dexie/Redux.
 *
 * @param {object}   ctx
 * @param {object}   ctx.storeCounts     { visits, invitations, employees, black_lists, comes_from, sync_meta }
 * @param {object[]} ctx.syncMeta        rows from db.sync_meta
 * @param {object[]} ctx.visits          rows from db.visits
 * @param {boolean}  ctx.isOnline        Redux state.utilities.isOnline
 * @param {boolean}  ctx.navigatorOnline navigator.onLine
 * @param {boolean}  [ctx.enabledVisitHost]
 * @param {number}   [ctx.now]           injectable clock for tests (default Date.now())
 * @returns {Array<{ id: string, titleKey: string, status: 'pass'|'fail'|'warn', detail: string|null }>}
 */
export function runLiveChecks({
  storeCounts = {},
  syncMeta = [],
  visits = [],
  isOnline,
  navigatorOnline,
  enabledVisitHost = false,
  now = Date.now(),
}) {
  const checks = []

  const missingStores = DEXIE_STORE_NAMES.filter(name => !(name in storeCounts))
  checks.push({
    id: 'stores_exist',
    titleKey: 'audit_check_stores_exist',
    status: missingStores.length === 0 ? 'pass' : 'fail',
    detail: missingStores.length ? missingStores.join(', ') : null,
  })

  checks.push({
    id: 'comes_from_not_empty',
    titleKey: 'audit_check_comes_from_not_empty',
    status: (storeCounts.comes_from ?? 0) > 0 ? 'pass' : 'warn',
    detail: `${storeCounts.comes_from ?? 0}`,
  })

  checks.push({
    id: 'black_lists_not_empty',
    titleKey: 'audit_check_black_lists_not_empty',
    status: (storeCounts.black_lists ?? 0) > 0 ? 'pass' : 'warn',
    detail: `${storeCounts.black_lists ?? 0}`,
  })

  checks.push({
    id: 'sync_meta_has_checkpoint',
    titleKey: 'audit_check_sync_meta_checkpoint',
    status: syncMeta.length > 0 ? 'pass' : 'fail',
    detail: `${syncMeta.length}`,
  })

  checks.push({
    id: 'online_state_consistent',
    titleKey: 'audit_check_online_consistency',
    status: navigatorOnline === isOnline ? 'pass' : 'warn',
    detail: `navigator.onLine=${navigatorOnline} · redux.isOnline=${isOnline}`,
  })

  const failedVisits = visits.filter(v => v.syncStatus === 'failed')
  checks.push({
    id: 'no_failed_visits',
    titleKey: 'audit_check_no_failed_visits',
    status: failedVisits.length === 0 ? 'pass' : 'fail',
    detail: failedVisits.length ? `${failedVisits.length}` : null,
  })

  const stuckPending = visits.filter(v => {
    if (v.syncStatus !== 'pending' || !v.entry_time) return false
    const entryTime = new Date(v.entry_time).getTime()
    if (Number.isNaN(entryTime)) return false
    return (now - entryTime) > ONE_HOUR_MS
  })
  checks.push({
    id: 'no_stuck_pending',
    titleKey: 'audit_check_no_stuck_pending',
    status: !isOnline ? 'warn' : (stuckPending.length === 0 ? 'pass' : 'fail'),
    detail: !isOnline ? null : (stuckPending.length ? `${stuckPending.length}` : null),
  })

  const syncedWithoutId = visits.filter(v => v.syncStatus === 'synced' && (v.id === null || v.id === undefined))
  checks.push({
    id: 'synced_have_id',
    titleKey: 'audit_check_synced_have_id',
    status: syncedWithoutId.length === 0 ? 'pass' : 'fail',
    detail: syncedWithoutId.length ? `${syncedWithoutId.length}` : null,
  })

  if (enabledVisitHost) {
    checks.push({
      id: 'employees_populated',
      titleKey: 'audit_check_employees_populated',
      status: (storeCounts.employees ?? 0) > 0 ? 'pass' : 'fail',
      detail: (storeCounts.employees ?? 0) === 0 ? 'BUG-01' : `${storeCounts.employees}`,
    })
  }

  return checks
}
